import { Button, Grid, SimpleGrid, Stack } from "@mantine/core";
import { Link } from "gatsby";
import React, { ReactNode } from "react";

import { Text, Title } from "@components";

import { MenuLink } from "@types";

interface MegaMenuProps {
  title: ReactNode;
  content: ReactNode;
  cta?: {
    label: string;
    href: string;
  };
  links: MenuLink[];
  light?: boolean;
}

export function MegaMenu({
  title,
  content,
  cta,
  links,
  light = false,
}: MegaMenuProps) {
  // Only use two columns if there are more than 5 links
  const useTwoColumns = links.length > 5;
  const midpoint = Math.ceil(links.length / 2);
  const leftLinks = useTwoColumns ? links.slice(0, midpoint) : links;
  const rightLinks = useTwoColumns ? links.slice(midpoint) : [];

  return (
    <Grid gutter="xl" justify="space-between" align="center">
      <Grid.Col span={3.5}>
        <Stack gap="md">
          {typeof title === "string" ? (
            <Title order={4} c={light ? "brandBlack" : "brandWhite"}>
              {title}
            </Title>
          ) : (
            title
          )}

          {typeof content === "string" ? (
            <Text
              c={light ? "fontDarkSecondary" : "fontLightSecondary"}
              size="xs"
            >
              {content}
            </Text>
          ) : (
            content
          )}

          {cta && (
            <Text
              size="xs"
              component={Link}
              td="underline"
              fw="600"
              to={cta.href}
            >
              {cta.label}
            </Text>
          )}
        </Stack>
      </Grid.Col>

      <Grid.Col span="content">
        <SimpleGrid cols={useTwoColumns ? 2 : 1} spacing="xs">
          <Stack gap="xs" miw={useTwoColumns ? "auto" : 400}>
            {leftLinks.map((link, index) => (
              <Button
                key={index}
                size="xs"
                variant={light ? "flat-square-light" : "flat-square"}
                leftSection={link.icon}
                justify="start"
                component={Link}
                to={link.href || "#"}
                w={useTwoColumns ? "auto" : "100%"}
              >
                {link.label}
              </Button>
            ))}
          </Stack>

          {useTwoColumns && (
            <Stack gap="xs">
              {rightLinks.map((link, index) => (
                <Button
                  key={index}
                  size="xs"
                  variant={light ? "flat-square-light" : "flat-square"}
                  leftSection={link.icon}
                  justify="start"
                  component={Link}
                  to={link.href || "#"}
                  w="auto"
                >
                  {link.label}
                </Button>
              ))}
            </Stack>
          )}
        </SimpleGrid>
      </Grid.Col>
    </Grid>
  );
}
